@if (!trayOpen()) {
  <div
    #stack
    class="vp-notification-stack"
    role="region"
    aria-label="New notifications"
    data-testid="notification-stack"
  >
    @if (trayEnabled() && signedIn() && store.urgentOverflow() > 0) {
      <button
        type="button"
        class="vp-notification-stack__overflow"
        data-testid="notification-urgent-overflow"
        (click)="openTray($event.detail === 0)"
      >
        +{{ store.urgentOverflow() }} more urgent
      </button>
    }
    @for (entry of stackEntries(); track entry.id) {
      @let n = entry.notification;
      <vp-toast
        appearance="floating"
        emphasis="solid"
        [testid]="'notification-toast-' + n.kind"
        [tone]="n.tone"
        [heading]="n.heading"
        [urgent]="isLocked(n)"
        [resolved]="n.resolved"
        [navigable]="isNavigable(n)"
        [dismissible]="!isLocked(n)"
        [tuckable]="trayEnabled() && n.sticky && !isLocked(n)"
        [countdownMs]="entry.countdownMs"
        [ariaLabel]="isNavigable(n) ? openLabel(n) : null"
        (opened)="open(n)"
        (dismissed)="store.dismiss(entry.id)"
        (tucked)="store.tuck(entry.id)"
        (click)="onToastClick(entry)"
        (mouseenter)="store.pause(entry.id)"
        (mouseleave)="store.resume(entry.id)"
        (focusin)="store.pause(entry.id)"
        (focusout)="onToastFocusOut(entry.id, $event)"
      >
        @if (n.html) {
          <span [innerHTML]="n.message"></span>
        } @else {
          {{ n.message }}
        }
        @if (n.sticky) {
          <vp-notification-meta vpToastMeta [notification]="n" />
        }
      </vp-toast>
    }
  </div>
}

@if (showTrigger()) {
  <button
    #trigger
    type="button"
    class="vp-notification-trigger"
    aria-controls="vp-notification-tray"
    data-testid="notification-trigger"
    [attr.aria-expanded]="trayOpen()"
    [attr.aria-label]="triggerLabel()"
    [attr.data-urgent]="store.urgentCount() > 0 ? '' : null"
    (click)="onTriggerClick($event)"
  >
    <span class="vp-notification-trigger__bell">
      <vp-icon name="bell" />
      @if (store.urgentCount() > 0) {
        <span class="vp-notification-trigger__dot" data-testid="notification-trigger-dot"></span>
      }
    </span>
    <span class="vp-notification-trigger__label">Notifications</span>
    <vp-count-pill [danger]="store.urgentCount() > 0" data-testid="notification-trigger-count">
      {{ store.trayCount() }}
    </vp-count-pill>
  </button>
}

<!-- After the trigger in DOM order, so Tab moves from the trigger into the open tray.
     Present whenever the trigger can be, so its aria-controls resolves. -->
@if (trayEnabled() && signedIn()) {
  <div
    id="vp-notification-tray"
    class="vp-notification-tray"
    role="dialog"
    aria-modal="false"
    aria-label="Notifications"
    data-testid="notification-tray"
    [hidden]="!trayOpen()"
  >
    <vp-dialog-shell heading="Notifications" [dense]="true" [noPad]="true">
      <vp-count-pill vpDialogHeaderRight data-testid="notification-tray-count">
        {{ store.trayCount() }}
      </vp-count-pill>
      <vp-action-btn
        vpDialogHeaderRight
        testid="notification-tray-dismiss-all"
        [disabled]="!store.hasDismissable()"
        (clicked)="store.dismissAll()"
      >
        Dismiss all
      </vp-action-btn>
      <vp-icon-btn
        vpDialogHeaderRight
        appearance="ghost"
        label="Collapse notifications"
        testid="notification-tray-collapse"
        (clicked)="close()"
      >
        <vp-icon name="chevron-down" />
      </vp-icon-btn>

      @if (store.tray().length > 0) {
        <div class="vp-notification-tray__list" role="list" data-testid="notification-tray-list">
          @for (n of store.tray(); track n.id) {
            <div
              class="vp-notification-tray__item"
              role="listitem"
              tabindex="-1"
              data-testid="notification-tray-item"
            >
              <vp-toast
                appearance="row"
                testid="notification-tray-toast"
                [tone]="n.tone"
                [heading]="n.heading"
                [urgent]="isLocked(n)"
                [resolved]="n.resolved"
                [navigable]="isNavigable(n)"
                [dismissible]="!isLocked(n)"
                [ariaLabel]="isNavigable(n) ? openLabel(n) : null"
                (opened)="open(n)"
                (dismissed)="store.dismiss(n.id)"
              >
                @if (n.html) {
                  <span [innerHTML]="n.message"></span>
                } @else {
                  {{ n.message }}
                }
                @if (n.sticky) {
                  <vp-notification-meta vpToastMeta [notification]="n" />
                }
              </vp-toast>
            </div>
          }
        </div>
      } @else {
        <p class="vp-notification-tray__empty" data-testid="notification-tray-empty">
          You're all caught up.
        </p>
      }

      @if (store.undoCount() !== null) {
        <div
          vpDialogFooter
          class="vp-notification-tray__footer"
          data-testid="notification-footer-undo"
        >
          <span>{{ store.undoCount() }} dismissed</span>
          <vp-action-btn testid="notification-undo-button" (clicked)="store.undoDismissAll()">
            Undo
          </vp-action-btn>
        </div>
      } @else if (store.clearedCount() > 0) {
        <div
          vpDialogFooter
          class="vp-notification-tray__footer"
          data-testid="notification-footer-cleared"
        >
          <span>{{ store.clearedCount() }} cleared elsewhere</span>
          <vp-action-btn
            testid="notification-dismiss-cleared-button"
            (clicked)="store.dismissCleared()"
          >
            Dismiss cleared
          </vp-action-btn>
        </div>
      }
    </vp-dialog-shell>
  </div>
}
